import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { Alert, LoadingBlock } from '../components/ui.tsx';
import { WorkPage, WorkSection, WorkEmpty, StaffNavigation } from '../components/work-page.tsx';
import { formatLocalTime, formatNumericDate } from '../i18n/datetime.ts';

export const timeOffCopy = {
  en: {
    title: 'Time off',
    back: 'Staff',
    refresh: 'Refresh',
    retry: 'Retry submission',
    intro: 'Dates and hours use this location’s time zone. All-day end dates are included.',
    privacy: 'Your optional reason is visible only to you and this location’s managers.',
    dates: 'Request time off',
    start: 'Start date',
    end: 'End date',
    allDay: 'All day',
    startTime: 'Start time',
    endTime: 'End time',
    reason: 'Reason (optional)',
    submit: 'Submit request',
    empty: 'No requests yet.',
    former: 'Former team member',
    own: 'Your request',
    pending: 'Pending',
    approved: 'Approved',
    declined: 'Declined',
    cancelled: 'Canceled',
    urgent: 'Starts within 24 hours',
    cancel: 'Cancel request',
    note: 'Decision note (optional)',
    approve: 'Approve',
    decline: 'Decline',
    affected: 'Review affected visits',
    affectedCount: 'Affected visits at this location:',
    visit: 'Review visit',
    truncated: 'More visits may be affected.',
    retained: 'Existing visits stay assigned. Review and resolve their conflicts separately.',
    approvalOff: 'Approval and cancellation of approved requests are currently unavailable.',
    saved: 'Request saved.',
    overlap: 'Other requests overlap this interval:',
    offline: 'Connect before submitting a request. Your draft stays here while this page is open.',
    failed: 'The submission could not be confirmed. Retry the same submission before making another change.',
    readFailed: 'Current requests could not be loaded. Refresh to try again.',
    conflict: 'The request changed or the interval conflicts. Review the current requests before trying again.',
    invalid: 'Check the dates and hours. Choose a valid range that has not ended, of at most 366 days.',
    unavailable: 'Time off is not available for this account and location.',
  },
  es: {
    title: 'Tiempo libre',
    back: 'Personal',
    refresh: 'Actualizar',
    retry: 'Reintentar envío',
    intro:
      'Las fechas y horas utilizan la zona horaria de esta ubicación. Se incluye la fecha final de los días completos.',
    privacy: 'Solo usted y los gerentes de esta ubicación pueden ver su motivo opcional.',
    dates: 'Solicitar tiempo libre',
    start: 'Fecha inicial',
    end: 'Fecha final',
    allDay: 'Todo el día',
    startTime: 'Hora inicial',
    endTime: 'Hora final',
    reason: 'Motivo (opcional)',
    submit: 'Enviar solicitud',
    empty: 'Todavía no hay solicitudes.',
    former: 'Exintegrante del equipo',
    own: 'Su solicitud',
    pending: 'Pendiente',
    approved: 'Aprobada',
    declined: 'Rechazada',
    cancelled: 'Cancelada',
    urgent: 'Comienza en las próximas 24 horas',
    cancel: 'Cancelar solicitud',
    note: 'Nota de decisión (opcional)',
    approve: 'Aprobar',
    decline: 'Rechazar',
    affected: 'Revisar visitas afectadas',
    affectedCount: 'Visitas afectadas en esta ubicación:',
    visit: 'Revisar visita',
    truncated: 'Puede haber más visitas afectadas.',
    retained: 'Las visitas existentes conservan sus asignaciones. Revise y resuelva sus conflictos por separado.',
    approvalOff: 'Actualmente no se pueden aprobar solicitudes ni cancelar solicitudes aprobadas.',
    saved: 'Solicitud guardada.',
    overlap: 'Otras solicitudes se superponen con este intervalo:',
    offline: 'Conéctese antes de enviar una solicitud. Su borrador permanece aquí mientras esta página esté abierta.',
    failed: 'No se pudo confirmar el envío. Reintente el mismo envío antes de realizar otro cambio.',
    readFailed: 'No se pudieron cargar las solicitudes actuales. Actualice para reintentarlo.',
    conflict:
      'La solicitud cambió o el intervalo presenta un conflicto. Revise las solicitudes actuales antes de reintentarlo.',
    invalid: 'Revise las fechas y horas. Elija un intervalo válido que no haya terminado, de hasta 366 días.',
    unavailable: 'Tiempo libre no está disponible para esta cuenta y ubicación.',
  },
} as const;
type Row = {
  id: string;
  name: string | null;
  own: boolean;
  urgent: boolean;
  starts_on: string;
  ends_on: string;
  all_day: number;
  start_time: string | null;
  end_time: string | null;
  timezone: string;
  reason: string;
  status: 'pending' | 'approved' | 'declined' | 'cancelled';
  decision_note: string | null;
};
type Listing = { items: Row[]; nextCursor: string | null; canManage: boolean; approvalEnabled: boolean };
type Affected = { items: { id: string }[]; truncated: boolean };
const button = 'btn btn-secondary';

export default function StaffTimeOffPage() {
  const s = useSession();
  return (
    <Content
      key={`${s.user?.companyId}:${s.user?.id}:${s.currentLocation?.id}:${s.user?.role}:${s.user?.permissions?.join(',')}:${s.company?.planFeatures?.join(',')}`}
    />
  );
}
function Content() {
  const { i18n } = useTranslation();
  const copy = timeOffCopy[i18n.language.startsWith('es') ? 'es' : 'en'];
  const [list, setList] = useState<Listing | null>(null),
    [canWrite, setCanWrite] = useState(false),
    [swaps, setSwaps] = useState(false);
  const [startsOn, setStartsOn] = useState(''),
    [endsOn, setEndsOn] = useState('');
  const [allDay, setAllDay] = useState(true),
    [startTime, setStartTime] = useState('09:00'),
    [endTime, setEndTime] = useState('17:00');
  const [reason, setReason] = useState(''),
    [notes, setNotes] = useState<Record<string, string>>({});
  const [affected, setAffected] = useState<Record<string, Affected>>({});
  const [error, setError] = useState(''),
    [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false),
    [uncertain, setUncertain] = useState(false);
  const alive = useRef(true),
    generation = useRef(0),
    accessEpoch = useRef(0),
    lock = useRef(false);
  const pending = useRef<{ path: string; body: unknown; creation: boolean } | null>(null);
  const denied = (error: unknown) => [401, 402, 403, 404].includes((error as { status?: number }).status ?? 0);
  function clearAccess() {
    accessEpoch.current++;
    generation.current++;
    pending.current = null;
    setList(null);
    setCanWrite(false);
    setSwaps(false);
    setStartsOn('');
    setEndsOn('');
    setAllDay(true);
    setStartTime('09:00');
    setEndTime('17:00');
    setReason('');
    setNotes({});
    setAffected({});
    setUncertain(false);
    setMessage('');
    setError(copy.unavailable);
  }
  useEffect(() => {
    alive.current = true;
    void refresh();
    return () => {
      alive.current = false;
      generation.current++;
    };
  }, []);
  async function refresh() {
    const gen = ++generation.current;
    try {
      const [cap, result] = await Promise.all([
        api.get<{ canWrite: boolean; swaps?: boolean }>('/staff/capability'),
        api.get<Listing>('/staff/time-off'),
      ]);
      if (!alive.current || gen !== generation.current) return;
      setCanWrite(cap.canWrite);
      setSwaps(cap.swaps === true);
      setList(result);
    } catch (e) {
      if (!alive.current || gen !== generation.current) return;
      if (denied(e)) clearAccess();
      else setError(copy.readFailed);
    }
  }
  async function mutate(path: string, body: unknown, creation = false) {
    if (lock.current || (pending.current && pending.current.path !== path)) return;
    if (!navigator.onLine) {
      setError(copy.offline);
      return;
    }
    lock.current = true;
    setBusy(true);
    setError('');
    setMessage('');
    pending.current ??= { path, body, creation };
    const epoch = accessEpoch.current;
    try {
      const result = await api.post<{ overlappingRequests?: number; affectedVisits?: Affected }>(
        pending.current.path,
        pending.current.body,
      );
      if (!alive.current || epoch !== accessEpoch.current) return;
      if (pending.current.creation) {
        setStartsOn('');
        setEndsOn('');
        setReason('');
      }
      if (result.affectedVisits && pending.current.path.endsWith('/decision')) {
        const id = pending.current.path.split('/').at(-2)!;
        setAffected((old) => ({ ...old, [id]: result.affectedVisits! }));
      }
      pending.current = null;
      setUncertain(false);
      setMessage(result.overlappingRequests ? `${copy.overlap} ${result.overlappingRequests}` : copy.saved);
      await refresh();
    } catch (e) {
      if (!alive.current || epoch !== accessEpoch.current) return;
      const status = (e as { status?: number }).status;
      if (denied(e)) {
        clearAccess();
      } else if (status && status >= 400 && status < 500 && status !== 408) {
        pending.current = null;
        setUncertain(false);
        setError(status === 409 ? copy.conflict : status === 400 ? copy.invalid : copy.unavailable);
        await refresh();
      } else {
        setUncertain(true);
        setError(copy.failed);
      }
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  }
  async function more() {
    if (!list?.nextCursor || lock.current) return;
    const gen = generation.current;
    lock.current = true;
    setBusy(true);
    try {
      const result = await api.get<Listing>(`/staff/time-off?before=${encodeURIComponent(list.nextCursor)}`);
      if (alive.current && gen === generation.current)
        setList((old) => old && { ...result, items: [...old.items, ...result.items] });
    } catch (e) {
      if (alive.current && gen === generation.current) {
        if (denied(e)) clearAccess();
        else setError(copy.readFailed);
      }
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  }
  async function review(id: string) {
    const gen = generation.current;
    try {
      const result = await api.get<Affected>(`/staff/time-off/${id}/affected`);
      if (alive.current && gen === generation.current) setAffected((old) => ({ ...old, [id]: result }));
    } catch (e) {
      if (alive.current && gen === generation.current) {
        if (denied(e)) clearAccess();
        else setError(copy.readFailed);
      }
    }
  }
  const disabled = busy || uncertain;
  return (
    <WorkPage
      title={copy.title}
      description={copy.intro}
      actions={
        <button
          className={button}
          disabled={busy}
          onClick={() => {
            setError('');
            void refresh();
          }}
        >
          {copy.refresh}
        </button>
      }
    >
      <StaffNavigation active="time-off" canManage={list?.canManage} ownDocuments={!list?.canManage} swaps={swaps} />
      <p className="text-sm text-[color:var(--color-muted)]">{copy.privacy}</p>
      {error && <Alert tone="error">{error}</Alert>}
      {message && <Alert tone="success">{message}</Alert>}
      {!list && !error && <LoadingBlock />}
      {uncertain && (
        <button
          className={button}
          disabled={busy}
          onClick={() => pending.current && void mutate(pending.current.path, pending.current.body)}
        >
          {copy.retry}
        </button>
      )}
      {list && (
        <>
          {!list.approvalEnabled && <p>{copy.approvalOff}</p>}
          {canWrite && (
            <form
              className="card space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                void mutate(
                  '/staff/time-off',
                  {
                    clientMutationId: crypto.randomUUID(),
                    startsOn,
                    endsOn,
                    allDay,
                    startTime: allDay ? null : startTime,
                    endTime: allDay ? null : endTime,
                    reason,
                  },
                  true,
                );
              }}
            >
              <h2 className="text-lg font-semibold">{copy.dates}</h2>
              <fieldset disabled={disabled} className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <label>
                    {copy.start}
                    <input
                      className="field-control mt-1"
                      type="date"
                      required
                      value={startsOn}
                      onChange={(e) => setStartsOn(e.target.value)}
                    />
                  </label>
                  <label>
                    {copy.end}
                    <input
                      className="field-control mt-1"
                      type="date"
                      required
                      min={startsOn}
                      value={endsOn}
                      onChange={(e) => setEndsOn(e.target.value)}
                    />
                  </label>
                </div>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
                  {copy.allDay}
                </label>
                {!allDay && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label>
                      {copy.startTime}
                      <input
                        className="field-control mt-1"
                        type="time"
                        required
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                      />
                    </label>
                    <label>
                      {copy.endTime}
                      <input
                        className="field-control mt-1"
                        type="time"
                        required
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                      />
                    </label>
                  </div>
                )}
                <label className="block">
                  {copy.reason}
                  <textarea
                    className="field-control mt-1"
                    maxLength={1000}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </label>
                <button className={button} type="submit">
                  {copy.submit}
                </button>
              </fieldset>
            </form>
          )}
          <WorkSection title={i18n.language.startsWith('es') ? 'Solicitudes' : 'Requests'}>
            {!list.items.length && !error && <WorkEmpty title={copy.empty} />}
            {list.items.map((row) => (
              <article key={row.id} className="work-row mb-3 space-y-3">
                <h3 className="font-bold">{row.own ? copy.own : (row.name ?? copy.former)}</h3>
                <p>
                  {formatNumericDate(row.starts_on)}{' '}
                  {row.all_day || !row.start_time ? '' : formatLocalTime(row.start_time)} /{' '}
                  {formatNumericDate(row.ends_on)} {row.all_day || !row.end_time ? '' : formatLocalTime(row.end_time)} (
                  {row.timezone})
                </p>
                <p>{copy[row.status]}</p>
                {row.urgent && <p>{copy.urgent}</p>}
                {row.reason && <p className="whitespace-pre-wrap break-words">{row.reason}</p>}
                {row.decision_note && <p className="whitespace-pre-wrap break-words">{row.decision_note}</p>}
                {row.own &&
                  canWrite &&
                  (row.status === 'pending' || (row.status === 'approved' && list.approvalEnabled)) && (
                    <button
                      className={button}
                      disabled={disabled}
                      onClick={() => void mutate(`/staff/time-off/${row.id}/cancel`, {})}
                    >
                      {copy.cancel}
                    </button>
                  )}
                {list.canManage && (
                  <>
                    <button className={button} disabled={disabled} onClick={() => void review(row.id)}>
                      {copy.affected}
                    </button>
                    {affected[row.id] && (
                      <p>
                        {copy.affectedCount} {affected[row.id]!.items.length}.{' '}
                        {affected[row.id]!.truncated && copy.truncated} {copy.retained}
                      </p>
                    )}
                    {affected[row.id]?.items.map((visit, index) => (
                      <a
                        key={visit.id}
                        className="block underline"
                        href={`/schedule?appointment=${encodeURIComponent(visit.id)}`}
                      >
                        {copy.visit} {index + 1}
                      </a>
                    ))}
                    {row.status === 'pending' && (
                      <fieldset disabled={disabled} className="space-y-3">
                        <label className="block">
                          {copy.note}
                          <textarea
                            className="field-control mt-1"
                            maxLength={1000}
                            value={notes[row.id] ?? ''}
                            onChange={(e) => setNotes((old) => ({ ...old, [row.id]: e.target.value }))}
                          />
                        </label>
                        <div className="flex flex-wrap gap-3">
                          <button
                            className={button}
                            disabled={!list.approvalEnabled || !affected[row.id]}
                            onClick={() =>
                              void mutate(`/staff/time-off/${row.id}/decision`, {
                                decision: 'approved',
                                note: notes[row.id] ?? '',
                              })
                            }
                          >
                            {copy.approve}
                          </button>
                          <button
                            className={button}
                            onClick={() =>
                              void mutate(`/staff/time-off/${row.id}/decision`, {
                                decision: 'declined',
                                note: notes[row.id] ?? '',
                              })
                            }
                          >
                            {copy.decline}
                          </button>
                        </div>
                        <p>{copy.retained}</p>
                      </fieldset>
                    )}
                  </>
                )}
              </article>
            ))}
            {list.nextCursor && (
              <button className={button} disabled={disabled} onClick={() => void more()}>
                {i18n.language.startsWith('es') ? 'Cargar más' : 'Load more'}
              </button>
            )}
          </WorkSection>
        </>
      )}
    </WorkPage>
  );
}
